/**
 * 매장 정보 편집기 (/setup/ 로그인 후).
 *
 * 서버가 ACF export 에서 만든 스키마(JSON)를 받아 폼을 렌더한다.
 * 지원 타입: bool, text, textarea, url, editor(Simple/Pro), number, choices, drinks, group(중첩 포함), repeater(중첩 포함).
 * [임시 저장] → automation 초안 보관, [실제 반영하기] → salon 에 저장.
 */

import { busy } from './busyButton';
import confirmPop from './confirmPop';
import { convertImage, createImagePicker } from './imagePicker';
import { createModeEditor, sanitizeModeHTML } from './modeEditor';
import { readOrder, syncEdges } from './initOrderList';
import { initSearchField } from './initSearchField';
import { showFlash } from './partnerForm';
import initScrollSpyNav from './scrollSpyNav';

const postAjax = async (root, action, data) => {
  const body = new URLSearchParams({ action, nonce: root.dataset.nonce, ...data });
  const response = await fetch(root.dataset.ajaxUrl, {
    method: 'POST',
    body,
    credentials: 'same-origin',
  });
  const json = await response.json().catch(() => null);

  if (!json || !json.success) {
    // 통합 관리 계정이 매장을 다시 골라야 한다(목록에서 빠졌거나 아직 안 골랐다) — 선택 화면으로
    if (json?.data?.reselect && json.data.redirect) {
      window.location.assign(json.data.redirect);
    }

    throw new Error(json?.data?.message || `요청 실패 (HTTP ${response.status})`);
  }

  return json.data;
};

// 미리보기 이미지 주소에 파일 수정 시각(?v=)을 붙인다 — 대표 사진 · 로고는 파일 이름이 정해져 있어
// 같은 주소에 다른 파일이 올라오면 브라우저가 옛 캐시를 보여 준다. 화면 표시에만 쓰고 저장값에는 넣지 않는다.
// 판(version)은 qrshop-api 1.33.2 부터 온다. 없으면 주소 그대로.
const withVersion = (url, version) => {
  if (!url || !version || url.startsWith('data:')) {
    return url || '';
  }

  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}`;
};

const el = (tag, className, text) => {
  const node = document.createElement(tag);

  if (className) {
    node.className = className;
  }

  if (text !== undefined) {
    node.textContent = text;
  }

  return node;
};

// 메뉴 숨김 처리: 편집기 최상단에 별도 스타일 카드로 노출한다
const HIDDEN_NAV_FIELD = 'hidden_navigation_items';

// 메뉴 설정 카드 — 편집기 맨 위에 이 순서로 따로 그린다.
// 숨김(hidden_navigation_items)은 기본으로 보이는 메뉴를 끄고, 노출(visible_navigation_items)은
// 기본으로 감춘 메뉴(events)를 켠다. 한 메뉴는 두 필드 중 한쪽에만 있다 (salon qrshop_is_shop_navigation_enabled)
const NAV_FIELDS = [HIDDEN_NAV_FIELD, 'visible_navigation_items'];

// 제목 줄에 [순서 변경] 버튼(순서 모달)을 두는 카드.
// 반복 행(FAQ · 가격의 시술 그룹) · 음료 행 · 편의 서비스 행을 옮긴다. 상품 정보는 전용 카드에서 붙인다
const ORDER_FIELDS = ['faq', 'drink', 'service', 'price'];

// 행 [삭제] · [전체 삭제] 를 알림창 대신 말풍선(confirmPop)으로 묻는 카드. 써 보고 다른 카드로 넓힌다
const POP_CONFIRM_FIELDS = ['faq', 'price'];

// 매장 소개 카드의 링크 입력 — salon 의 ACF `site` 그룹 서브필드와 키가 1:1 이다.
// wechat 은 URL 이 아니라 ID 라서 바로가기 대신 복사 버튼을 붙인다.
const SITE_LINKS = [
  { key: 'homepage', label: '공식 홈페이지' },
  { key: 'smartplace', label: '네이버 스마트플레이스' },
  { key: 'blog', label: '네이버 블로그' },
  { key: 'instagram', label: '인스타그램' },
  { key: 'threads', label: '쓰레드' },
  { key: 'youtube', label: '유튜브' },
  { key: 'line', label: '라인' },
  { key: 'whatsapp', label: '왓츠앱' },
  { key: 'wechat', label: 'WeChat ID', kind: 'text' },
];

// 매장 테마 카드 — salon 의 ACF `theme` 그룹(chain · bg · brand · brand_dark)과 최상위 use_cart · use_call.
// 로고는 매장 글의 대표이미지다(salon 헤더가 get_the_post_thumbnail_url 로 그린다). 색 기본값은 ACF 필드 기본값과 같다
const THEME_COLORS = [
  { key: 'bg', label: '배경 컬러', fallback: '#f5f5f5' },
  { key: 'brand', label: '브랜드 컬러', fallback: '#222222' },
  { key: 'brand_dark', label: '브랜드 컬러(어두운 컬러)', fallback: '#000000' },
];

const THEME_SWITCHES = [
  { key: 'use_cart', label: '장바구니' },
  { key: 'use_call', label: '직원호출' },
];

const LOGO_TYPES = ['image/svg+xml', 'image/webp', 'image/png', 'image/jpeg'];
const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

// 라벨 줄: 라벨(좌) + 보조 설명(우)
const fieldHead = (label, hint) => {
  const head = el('div', 'field-head');
  head.append(el('span', 'field-label', label));

  if (hint) {
    head.append(el('span', 'field-hint', hint));
  }

  return head;
};

// 스위치 토글 — 숨긴 체크박스 + 트랙(ON/OFF 는 CSS content)
const renderToggle = (checked, caption) => {
  const wrap = el('label', 'toggle');

  if (caption) {
    wrap.append(el('span', 'toggle__label', caption));
  }

  const input = el('input', 'toggle__input');
  input.type = 'checkbox';
  input.dataset.input = '';
  input.checked = Boolean(checked);

  const track = el('span', 'toggle__track');
  track.setAttribute('aria-hidden', 'true');

  wrap.append(input, track);
  return wrap;
};

// 작은 스위치 — 글자(좌) + 스위치(우). 값 하나를 켜고 끄는 자리에 쓴다(대표 · 디자이너 전용)
const renderMiniToggle = (checked, caption) => {
  const wrap = el('label', 'toggle toggle--mini');
  wrap.append(el('span', 'toggle__label', caption));

  const input = el('input', 'toggle__input');
  input.type = 'checkbox';
  input.checked = Boolean(checked);

  const track = el('span', 'toggle__track');
  track.setAttribute('aria-hidden', 'true');

  wrap.append(input, track);
  return wrap;
};

// 색 입력 — 색 칸(브라우저 색 고르기)과 #RRGGBB 글자 입력이 서로 값을 따라간다.
// 저장값은 늘 올바른 색인 색 칸([data-theme-color])에서 읽는다
const renderColorField = ({ key, label, fallback }, value) => {
  const color = HEX_COLOR.test(value || '') ? value.toLowerCase() : fallback;
  const field = el('div', 'field');
  field.append(fieldHead(label));

  const box = el('div', 'colorfield');
  const swatch = el('input', 'colorfield__swatch');
  swatch.type = 'color';
  swatch.value = color;
  swatch.dataset.themeColor = key;
  swatch.setAttribute('aria-label', `${label} 고르기`);

  const input = el('input', 'colorfield__input');
  input.type = 'text';
  input.maxLength = 7;
  input.spellcheck = false;
  input.autocapitalize = 'off';
  input.value = color;
  input.setAttribute('aria-label', label);

  const typed = () => {
    const raw = input.value.trim();
    const hex = raw.startsWith('#') ? raw : `#${raw}`;

    return HEX_COLOR.test(hex) ? hex.toLowerCase() : '';
  };

  swatch.addEventListener('input', () => {
    input.value = swatch.value;
  });

  // 올바른 색이 되는 순간 색 칸도 바꾼다. 색 칸 값이 바뀌어야 자동 저장이 그 값을 읽는다
  input.addEventListener('input', () => {
    if (typed()) {
      swatch.value = typed();
    }
  });

  // 입력을 마쳤는데 형식이 틀리면 색 칸 값으로 되돌린다
  input.addEventListener('change', () => {
    input.value = typed() || swatch.value;
  });

  box.append(swatch, input);
  field.append(box);

  return field;
};

const choiceLabel = (choice, labels) => labels?.[choice] || choice;

// 체크한 항목을 기억한 순서대로 읽는다. 순서에 없는 항목(새로 체크)은 격자 순서대로 뒤에 붙는다
const checkedChoices = (wrap) => {
  let order = [];

  try {
    order = JSON.parse(wrap.dataset.choiceOrder || '[]');
  } catch (error) {
    order = [];
  }

  const rank = (value) => (order.includes(value) ? order.indexOf(value) : order.length);

  return Array.from(wrap.querySelectorAll('[data-choice-list] input:checked'))
    .map((input, index) => [input.value, index])
    .sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1])
    .map(([value]) => value);
};

// ── 음료 리스트: items 한 줄 = "이름|h,i" (h=🔥, i=🧊). salon 의 표기와 같다 ──

const parseDrinks = (text) =>
  String(text ?? '')
    .split(/\r?\n/)
    .map((line) => {
      const [name, flags = ''] = line.split('|');
      const codes = flags.split(',').map((code) => code.trim().toLowerCase());

      return { name: name.trim(), hot: codes.includes('h'), ice: codes.includes('i') };
    })
    .filter((drink) => '' !== drink.name);

const drinkLine = ({ name, hot, ice }) => {
  const flags = [hot && 'h', ice && 'i'].filter(Boolean).join(',');

  return flags ? `${name}|${flags}` : name;
};

// 숫자 입력(회원혜택 금액 등): 숫자만 허용 + 천단위 콤마 자동 표기.
// 저장 시에는 수집 단계에서 콤마를 떼고 숫자만 보낸다.
const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '');

const withThousands = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const attachAmountFormat = (input) => {
  input.addEventListener('input', () => {
    const caret = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = digitsOnly(input.value.slice(0, caret)).length;
    input.value = withThousands(digitsOnly(input.value));

    // 콤마가 늘거나 줄어도 앞쪽 숫자 개수를 기준으로 캐럿을 되돌린다
    let position = 0;
    let seen = 0;

    while (position < input.value.length && seen < digitsBeforeCaret) {
      if (/\d/.test(input.value[position])) {
        seen += 1;
      }

      position += 1;
    }

    input.setSelectionRange(position, position);
  });
};

// 라벨에 맞는 목적격 조사(을/를)를 붙인 기본 placeholder
const defaultPlaceholder = (label) => {
  if (!label) {
    return '';
  }

  const last = label.charCodeAt(label.length - 1) - 0xac00;
  const hasFinal = last >= 0 && last <= 11171 && last % 28 !== 0;

  return `${label}${hasFinal ? '을' : '를'} 입력하세요.`;
};

// 링크글 버튼 — 라벨 줄이나 행 오른쪽에 붙는 글자 행동 (.linktext)
const linkButton = (label) => {
  const button = el('button', 'linktext', label);
  button.type = 'button';
  return button;
};

// 행 순서 번호 — 숫자는 CSS 카운터가 세므로 행이 오가도 손댈 것이 없다
const rowOrderBadge = () => {
  const badge = el('span', 'qrshop-row__order');
  badge.setAttribute('aria-hidden', 'true');
  return badge;
};

// 순서 배지 옆 제목 — 바로 아래 입력에 값이 있으면 그 값을 보여 준다.
// 비었을 때는 empty(상품 행은 원본 상품명 = placeholder 값)를, 그것도 없으면 라벨을 둔다.
const bindRowTitle = (label, badge, input, empty = '') => {
  const labelText = label.textContent.trim();
  const fallback = empty.trim() || labelText;
  const text = el('span', 'qrshop-row__title-text', fallback);

  label.textContent = '';
  label.classList.add('qrshop-row__title');
  label.append(badge, text);

  if (!input) {
    return;
  }

  const sync = () => {
    const value = input.value.trim();
    text.textContent = '' === value ? fallback : value;
  };

  input.addEventListener('input', sync);
  sync();
};

// 행 삭제 — 밑줄 글자(.linktext) '삭제'. 무엇을 지우는지는 스크린 리더 이름으로 붙인다
const removeTextButton = (label) => {
  const button = linkButton('삭제');
  button.setAttribute('aria-label', label);
  return button;
};

const iconButton = (className, label, svgPath) => {
  const button = el('button', className);
  button.type = 'button';
  button.setAttribute('aria-label', label);
  button.title = label;
  button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="${svgPath}"/></svg>`;
  return button;
};

// ── 필드 렌더러: 스키마 노드 → DOM (data-field 래퍼로 감싸 수집 시 재귀 탐색) ──

const renderField = (name, spec, value, options = {}) => {
  const wrap = el('div');
  wrap.dataset.field = name;
  wrap.dataset.type = spec.type;
  const label = spec.label || name;

  if (spec.type === 'bool') {
    const row = el('div', 'field-head');
    row.append(el('span', 'field-label', label), renderToggle(value));
    wrap.append(row);
    return wrap;
  }

  // 워드프레스 편집기 칸(ACF wysiwyg) — 매장 설명글과 같은 Simple / Pro 편집기. 값은 {body, mode, locked}.
  // 인스턴스는 wrap.modeEditor 에 두고 수집 · mount · destroy 가 꺼내 쓴다
  if (spec.type === 'editor') {
    const head = fieldHead(label);
    wrap.append(head);

    // 원장 권한으로 저장하면 사라지는 태그(iframe 등)가 있는 글 — 고치지 않고 보내지도 않는다
    if (value?.locked) {
      wrap.append(
        el(
          'p',
          'message',
          '매장 사이트 관리자 화면에서 넣은 요소(동영상 등)가 있어 여기서는 고칠 수 없습니다. 기존 내용은 그대로 유지됩니다.'
        )
      );
      return wrap;
    }

    const editor = createModeEditor({
      html: value?.body || '',
      mode: value?.mode || 'simple',
      placeholder: `${label}을 입력하세요.`,
    });
    head.append(editor.switcher);
    wrap.append(editor.element);
    wrap.modeEditor = editor;
    return wrap;
  }

  if (spec.type === 'url') {
    wrap.append(fieldHead(label));
    const input = el('input', 'w-full px-4');
    input.type = 'text';
    input.inputMode = 'url';
    input.autocapitalize = 'off';
    input.spellcheck = false;
    input.dataset.input = '';
    input.placeholder = spec.placeholder || 'https://';
    input.value = value === undefined || value === null ? '' : String(value);
    wrap.append(input);
    return wrap;
  }

  if (spec.type === 'text' || spec.type === 'number') {
    wrap.append(fieldHead(label));
    const input = el('input', 'w-full px-4');
    input.type = 'text';
    input.dataset.input = '';
    input.placeholder = spec.placeholder || defaultPlaceholder(label);
    input.value = value === undefined || value === null ? '' : String(value);

    if (spec.type === 'number') {
      input.inputMode = 'numeric';
      input.autocomplete = 'off';
      input.value = withThousands(digitsOnly(input.value));
      attachAmountFormat(input);
    }

    wrap.append(input);
    return wrap;
  }

  if (spec.type === 'textarea') {
    // 마크업 안내는 매장 소개 설명글에만 두고, 반복 행·그룹 안에서는 라벨만 노출한다
    wrap.append(fieldHead(label, spec.instructions));
    const input = el('textarea', 'w-full px-4');
    input.rows = 5;
    input.dataset.input = '';
    input.placeholder = spec.placeholder || defaultPlaceholder(label);
    input.value = value === undefined || value === null ? '' : String(value);
    wrap.append(input);
    return wrap;
  }

  if (spec.type === 'choices') {
    wrap.append(fieldHead(label));

    const list = el('div', 'choice-list');
    list.dataset.choiceList = '';
    const selected = new Set(Array.isArray(value) ? value : []);
    // 체크한 항목의 순서 — 격자는 목록 순서 그대로 두고, 저장값 순서만 따로 기억한다 (checkedChoices)
    wrap.dataset.choiceOrder = JSON.stringify(Array.from(selected));

    (spec.choices || []).forEach((choice) => {
      const item = el('label', 'choice');
      const input = el('input', 'qrshop-checkbox');
      input.type = 'checkbox';
      input.value = choice;
      input.checked = selected.has(choice);
      item.append(input, document.createTextNode(choiceLabel(choice, spec.labels)));
      list.append(item);
    });

    wrap.append(list);
    return wrap;
  }

  if (spec.type === 'drinks') {
    wrap.append(renderDrinkList(parseDrinks(value), spec));
    return wrap;
  }

  if (spec.type === 'gallery') {
    wrap.append(fieldHead(label, '[임시 저장] 하면 업로드 · 화살표로 순서 변경'));

    const grid = el('div', 'qrshop-gallery');
    grid.dataset.gallery = '';

    (Array.isArray(value) ? value : []).forEach((photo) => {
      grid.append(renderGalleryTile(photo));
    });

    const fileInput = el('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.multiple = true;
    fileInput.hidden = true;

    const addButton = el('button', 'qrshop-gallery__add');
    addButton.type = 'button';
    addButton.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" class="size-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg><span class="text-xs">사진 추가</span>';
    addButton.addEventListener('click', () => fileInput.click());

    fileInput.addEventListener('change', async () => {
      const files = Array.from(fileInput.files || []);
      fileInput.value = '';

      for (const file of files) {
        // 선택 순서를 유지하기 위해 하나씩 변환한다
        // eslint-disable-next-line no-await-in-loop
        const photo = await stageGalleryPhoto(file);

        if (photo) {
          grid.insertBefore(renderGalleryTile(photo), addButton);
        }
      }
    });

    grid.append(addButton, fileInput);
    wrap.append(grid);
    return wrap;
  }

  if (spec.type === 'group') {
    const sub = el('div', 'space-y-5');
    sub.dataset.sub = '';

    Object.entries(spec.fields).forEach(([subName, subSpec]) => {
      // 사용여부는 카드 헤더로 올려 렌더한다 (render 에서 처리)
      if (options.skipUse && 'use' === subName) {
        return;
      }

      const field = renderField(subName, subSpec, value?.[subName], {
        nested: options.nested,
        popConfirm: options.popConfirm,
      });

      // 그룹 안의 그룹(리뷰 이벤트의 네이버 · 구글 등)은 제목을 단 상자로 묶는다 — 같은 이름의 입력이 반복되기 때문
      if ('group' === subSpec.type) {
        const groupLabel = subSpec.label || subName;
        field.classList.add('qrshop-row');
        field.prepend(el('h3', 'field-label', groupLabel));

        // 상자 하단 [내용 삭제] — 이 상자의 입력을 모두 비운다. 반영해야 매장 사이트에서도 지워진다
        const actions = el('div', 'qrshop-row__actions');
        const clear = linkButton('내용 삭제');
        clear.setAttribute('aria-label', `${groupLabel} 내용 삭제`);
        clear.addEventListener('click', async () => {
          if (!(await confirmPop(clear, { message: '내용을 지울까요?', confirmLabel: '삭제' }))) {
            return;
          }

          field.querySelectorAll('input, textarea, select').forEach((input) => {
            if ('checkbox' === input.type || 'radio' === input.type) {
              input.checked = false;
            } else {
              input.value = '';
            }
          });

          field.querySelectorAll('[data-type="editor"]').forEach((wrap) => {
            wrap.modeEditor?.setContent('', 'simple');
          });

          // 자동 저장 · 네비 상태가 알아채도록
          field.dispatchEvent(new Event('change', { bubbles: true }));
        });
        actions.append(clear);
        field.append(actions);
      }

      sub.append(field);
    });

    wrap.append(sub);
    return wrap;
  }

  if (spec.type === 'repeater') {
    // 중첩 반복(예: 시술 그룹 > 요금제)은 라벨 줄 없이 행과 추가 버튼만 둔다
    const clearButton = linkButton('전체 삭제');

    if (!options.nested) {
      const head = el('div', 'mb-2 flex items-center justify-between');
      head.append(el('span', 'field-label', label), clearButton);
      wrap.append(head);
    }

    const rows = el('div', 'space-y-3');
    rows.dataset.rows = '';
    rows.classList.add('qrshop-rows');

    clearButton.addEventListener('click', async () => {
      const count = rows.children.length;

      if (!count) {
        return;
      }

      const ok = options.popConfirm
        ? await confirmPop(clearButton, {
            message: `항목 ${count}개를 모두 삭제할까요?`,
            confirmLabel: '삭제',
          })
        : window.confirm(`${spec.label || name} 항목을 전체 삭제할까요?`);

      if (ok) {
        rows.textContent = '';
        // 자동 저장 · 네비 상태가 알아채도록
        rows.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    (Array.isArray(value) ? value : []).forEach((rowValue) => {
      rows.append(renderRow(spec, rowValue, options));
    });

    const addButton = el(
      'button',
      'button button--muted button--block mt-3',
      `+ ${spec.button || '추가'}`
    );
    addButton.type = 'button';
    addButton.addEventListener('click', () => rows.append(renderRow(spec, {}, options)));

    wrap.append(rows, addButton);
    return wrap;
  }

  return wrap;
};

// 참고이미지(gallery) — 타일 하나. 좌우 이동 + 삭제.
// photo.id = 이미 매장에 올라간 첨부 / photo.temp = 반영 시 업로드할 대기 이미지
const renderGalleryTile = (photo) => {
  const tile = el('div', 'qrshop-gallery__tile');

  if (photo.temp) {
    tile.dataset.photoTemp = photo.temp;
    tile.append(el('span', 'qrshop-gallery__badge', '저장 대기'));
  } else {
    tile.dataset.photoId = String(photo.id);
  }

  const image = el('img');
  image.src = photo.url || '';
  image.alt = '';
  image.loading = 'lazy';

  const actions = el('div', 'qrshop-gallery__actions');

  const move = (direction) => {
    const sibling = direction < 0 ? tile.previousElementSibling : tile.nextElementSibling;

    // 추가 버튼은 항상 마지막이라 그 앞까지만 이동한다
    if (!sibling || (!sibling.dataset.photoId && !sibling.dataset.photoTemp)) {
      return;
    }

    if (direction < 0) {
      tile.parentElement.insertBefore(tile, sibling);
    } else {
      tile.parentElement.insertBefore(sibling, tile);
    }
  };

  const prev = iconButton('qrshop-gallery__button', '앞으로 이동', 'M15.75 19.5L8.25 12l7.5-7.5');
  prev.addEventListener('click', () => move(-1));

  const next = iconButton('qrshop-gallery__button', '뒤로 이동', 'M8.25 4.5l7.5 7.5-7.5 7.5');
  next.addEventListener('click', () => move(1));

  const remove = iconButton(
    'qrshop-gallery__button qrshop-gallery__button--danger',
    '삭제',
    'M6 18L18 6M6 6l12 12'
  );
  remove.addEventListener('click', () => tile.remove());

  actions.append(prev, next, remove);
  tile.append(image, actions);
  return tile;
};

// 대기 이미지: 임시 키 → data URL. 반영 성공 후 비운다.
const stagedPhotos = new Map();
let stagedPhotoSeq = 0;

// 선택한 이미지를 webp 로 변환해 대기 목록에 올린다(업로드는 [실제 반영하기] 때).
// 구현은 편집기 초기화에서 주입한다(상태 메시지 출력이 필요하다).
let stageGalleryPhoto = async () => null;

// 행 렌더러도 토스트를 띄울 수 있게 초기화에서 주입한다
let showStatus = () => {};

// 음료 한 행 — 음료명 입력 · 🔥 · 🧊 체크 · 행 버튼(−/+). 편의 서비스(spec.temps false)는 온도 칸이 없다
const renderDrinkRow = (drink, buttonLabel, buttonText, spec) => {
  const row = el('div', 'drink-row');

  const name = el('input', 'drink-row__name');
  name.type = 'text';
  name.dataset.drinkName = '';
  name.placeholder = `${spec?.noun || '음료'}명을 입력 하세요.`;
  name.maxLength = 50;
  name.value = drink.name;

  const temp = (key, icon, caption) => {
    const item = el('label', 'choice drink-row__temp');
    item.title = caption;
    const input = el('input');
    input.type = 'checkbox';
    input.dataset.drinkTemp = key;
    input.checked = drink[key];
    input.setAttribute('aria-label', caption);
    const emoji = el('span', '', icon);
    emoji.setAttribute('aria-hidden', 'true');
    item.append(input, emoji);
    return item;
  };

  const button = el('button', 'button button--muted button--icon drink-row__button', buttonText);
  button.type = 'button';
  button.setAttribute('aria-label', buttonLabel);
  button.title = buttonLabel;

  if (false === spec?.temps) {
    row.append(name, button);
  } else {
    row.append(name, temp('hot', '🔥', 'HOT'), temp('ice', '🧊', 'ICE'), button);
  }
  return { row, name, button };
};

const readDrinkRow = (row) => ({
  // 줄바꿈 · 구분자(|)는 items 표기를 깨므로 이름에서 뺀다
  name: row
    .querySelector('[data-drink-name]')
    .value.replace(/[|\r\n]/g, ' ')
    .trim(),
  hot: row.querySelector('[data-drink-temp="hot"]')?.checked ?? false,
  ice: row.querySelector('[data-drink-temp="ice"]')?.checked ?? false,
});

// 음료 · 편의 서비스 리스트 — 등록한 행(−) 목록 + 맨 아래 새 항목 입력 행(+)
const renderDrinkList = (drinks, spec) => {
  const noun = spec?.noun || '음료';
  const box = el('div', 'drink-list');
  const rows = el('div', 'drink-list__rows');
  rows.dataset.drinkRows = '';

  // 행을 넣고 빼는 것도 편집이다 — 자동 저장이 듣는 change 를 올려 보낸다
  const notify = () => box.dispatchEvent(new Event('change', { bubbles: true }));

  const addRow = (drink) => {
    const { row, button } = renderDrinkRow(drink, `${drink.name} 삭제`, '−', spec);
    button.classList.add('drink-row__button--remove');
    button.addEventListener('click', () => {
      row.remove();
      notify();
    });
    rows.append(row);
  };

  drinks.forEach(addRow);

  const draft = renderDrinkRow({ name: '', hot: true, ice: true }, `${noun} 추가`, '+', spec);
  draft.row.classList.add('drink-row--new');
  draft.row.dataset.drinkNew = '';

  // 주스 · 쥬스로 끝나는 음료는 ICE 전용 — 이름이 그렇게 바뀌는 순간 🧊 만 체크한다.
  // 자동으로 바꾼 체크는 이름이 주스가 아니게 되거나(한글 조합 중 잠깐 "주스" 로 끝난 경우 포함) 음료를 추가하면 되돌리고,
  // 직접 바꾼 체크는 건드리지 않는다
  const hot = draft.row.querySelector('[data-drink-temp="hot"]');
  const ice = draft.row.querySelector('[data-drink-temp="ice"]');
  let juice = false;
  let autoTemp = false;

  const setTemps = (hotChecked, iceChecked) => {
    if (hot && ice) {
      hot.checked = hotChecked;
      ice.checked = iceChecked;
    }
  };

  const resetJuice = () => {
    if (autoTemp) {
      setTemps(true, true);
    }

    juice = false;
    autoTemp = false;
  };

  draft.name.addEventListener('input', () => {
    const isJuice = /(주스|쥬스)$/.test(draft.name.value.trim());

    if (isJuice && !juice) {
      setTemps(false, true);
      autoTemp = true;
    } else if (!isJuice && juice && autoTemp) {
      setTemps(true, true);
      autoTemp = false;
    }

    juice = isJuice;
  });

  [hot, ice].forEach((input) => input?.addEventListener('change', () => (autoTemp = false)));

  const submit = () => {
    const drink = readDrinkRow(draft.row);

    if ('' === drink.name) {
      showStatus(`${noun}명을 입력하세요.`, 'warning');
      draft.name.focus();
      return;
    }

    const same = Array.from(rows.children).find((row) => readDrinkRow(row).name === drink.name);

    if (same) {
      showStatus(`${drink.name} 은(는) 이미 목록에 있습니다.`, 'warning');
      same.querySelector('[data-drink-name]').focus();
      return;
    }

    addRow(drink);
    notify();
    draft.name.value = '';
    resetJuice();
    draft.name.focus();
  };

  draft.button.addEventListener('click', submit);
  draft.name.addEventListener('keydown', (event) => {
    // 한글 조합 중 Enter 는 조합 확정이므로 건너뛴다
    if ('Enter' === event.key && !event.isComposing) {
      event.preventDefault();
      submit();
    }
  });

  box.append(rows, draft.row);
  return box;
};

// 메뉴 숨김 · 노출 처리 전용 렌더러 — 안내 박스 + 가로 체크박스 한 줄
const renderNavField = (name, spec, value) => {
  const wrap = el('div');
  wrap.dataset.field = name;
  wrap.dataset.type = spec.type;

  if (spec.instructions) {
    wrap.append(el('p', 'message', spec.instructions));
  }

  const list = el('div', 'card__choices');
  list.dataset.choiceList = '';
  const selected = new Set(Array.isArray(value) ? value : []);

  (spec.choices || []).forEach((choice) => {
    const label = el('label', 'choice');
    const input = el('input');
    input.type = 'checkbox';
    input.value = choice;
    input.checked = selected.has(choice);
    label.append(input, document.createTextNode(spec.labels?.[choice] || choice));
    list.append(label);
  });

  wrap.append(list);
  return wrap;
};

const renderRow = (repeaterSpec, rowValue, options = {}) => {
  const row = el('div', 'qrshop-row');
  row.dataset.row = '';

  // 순서 번호 — 첫 필드 라벨 앞에 붙인다 (순서 변경은 카드 제목 줄의 순서 모달에서 한다)
  const orderBadge = rowOrderBadge();

  const removeLabel = `${(repeaterSpec.button || '항목').replace(/추가$/, '삭제').trim()}`;
  const remove = removeTextButton(removeLabel);
  // 묶음 행(가격 정보의 시술 그룹 — 버튼이 '… 그룹 추가')은 [그룹 삭제] 로 부르고 그룹이라고 묻는다
  const isGroupRow = /그룹\s*추가$/.test(repeaterSpec.button || '');

  if (isGroupRow) {
    remove.textContent = '그룹 삭제';
  }

  // 말풍선에 넣을 그룹 이름 — 누르는 순간 행 제목과 같은 첫 필드(시술명) 값. 비면 넣지 않고, 길면 말풍선이 넘치지 않게 줄인다
  const groupName = () => {
    const name = (firstField?.querySelector(':scope > [data-input]')?.value || '').trim();

    if ('' === name) {
      return '';
    }

    return `[${name.length > 20 ? `${name.slice(0, 20)}…` : name}]`;
  };

  remove.addEventListener('click', async () => {
    // 말풍선 카드만 묻는다 — 나머지는 전처럼 바로 지운다
    if (
      options.popConfirm &&
      !(await confirmPop(remove, {
        message: isGroupRow ? `이 그룹${groupName()}을 삭제할까요?` : '이 항목을 삭제할까요?',
        confirmLabel: '삭제',
      }))
    ) {
      return;
    }

    const list = row.parentElement;
    row.remove();
    list?.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // 행 안은 2열 그리드 — ACF 필드 폭이 50% 미만인 필드만 한 칸을 차지한다
  const body = el('div', 'grid grid-cols-2 gap-x-3 gap-y-4');
  body.dataset.sub = '';

  const hoistedBools = [];
  let firstField = null;

  Object.entries(repeaterSpec.fields).forEach(([name, spec]) => {
    // 행 안의 on/off 값(예: 대표)은 첫 필드의 라벨 줄 우측으로 올린다
    if ('bool' === spec.type) {
      hoistedBools.push([name, spec]);
      return;
    }

    const field = renderField(name, spec, rowValue?.[name], {
      nested: true,
      popConfirm: options.popConfirm,
    });
    field.classList.add(spec.width > 0 && spec.width <= 50 ? 'col-span-1' : 'col-span-2');
    body.append(field);
    firstField = firstField || field;
  });

  hoistedBools.forEach(([name, spec]) => {
    const wrapper = el('div');
    wrapper.dataset.field = name;
    wrapper.dataset.type = spec.type;
    wrapper.dataset.rowBool = name;

    const item = renderMiniToggle(rowValue?.[name], spec.label || name);
    item.querySelector('.toggle__input').dataset.rowBoolInput = '';
    wrapper.append(item);

    const target = firstField?.querySelector(':scope > .field-head');

    if (target) {
      target.append(wrapper);
    } else {
      body.append(wrapper);
    }
  });

  const titleLabel = firstField?.querySelector(':scope > .field-head > .field-label');

  if (titleLabel) {
    bindRowTitle(titleLabel, orderBadge, firstField.querySelector(':scope > [data-input]'));
  }

  const actions = el('div', 'qrshop-row__actions');
  actions.append(remove);

  row.append(body, actions);
  return row;
};

// ── 수집: DOM → 값 (렌더와 같은 구조를 역으로 탐색) ─────────────────────────

const collectField = (wrap, spec) => {
  if (spec.type === 'bool') {
    return wrap.querySelector('[data-input]').checked;
  }

  if (
    spec.type === 'text' ||
    spec.type === 'url' ||
    spec.type === 'number' ||
    spec.type === 'textarea'
  ) {
    // 라벨 줄로 올린 체크박스가 앞에 오는 경우가 있어 직계 입력만 읽는다
    const raw = wrap.querySelector(':scope > [data-input]').value;

    return 'number' === spec.type ? digitsOnly(raw) : raw;
  }

  if (spec.type === 'choices') {
    return checkedChoices(wrap);
  }

  // 잠긴 글은 편집기가 없어 undefined — JSON 에서 빠지고 매장 사이트는 기존 글을 둔다
  if (spec.type === 'editor') {
    return wrap.modeEditor
      ? { body: wrap.modeEditor.getHTML(), mode: wrap.modeEditor.getMode() }
      : undefined;
  }

  if (spec.type === 'drinks') {
    // [+] 를 누르지 않고 입력만 해 둔 새 음료도 잃지 않게 함께 담는다
    return Array.from(wrap.querySelectorAll('[data-drink-rows] > .drink-row, [data-drink-new]'))
      .map(readDrinkRow)
      .filter((drink) => '' !== drink.name)
      .map(drinkLine)
      .join('\n');
  }

  if (spec.type === 'gallery') {
    return Array.from(
      wrap.querySelectorAll('[data-gallery] [data-photo-id], [data-gallery] [data-photo-temp]')
    ).map((tile) =>
      tile.dataset.photoTemp
        ? { data: stagedPhotos.get(tile.dataset.photoTemp) || '' }
        : // URL 도 같이 담는다 — 초안으로 저장하거나 미리보기를 그릴 때 다시 필요하다.
          // 임시 저장으로 올린 사진은 아직 매장 필드에 없어 서버 응답에서 URL 을 알 수 없다.
          { id: Number(tile.dataset.photoId), url: tile.querySelector('img')?.src || '' }
    );
  }

  if (spec.type === 'group') {
    return collectFields(wrap.querySelector(':scope > [data-sub]'), spec.fields);
  }

  if (spec.type === 'repeater') {
    return Array.from(wrap.querySelector(':scope > [data-rows]').children).map((row) => {
      const values = collectFields(row.querySelector(':scope > [data-sub]'), spec.fields);

      // 라벨 줄로 올린 on/off 값은 [data-sub] 밖에 있으므로 따로 읽는다
      row.querySelectorAll('[data-row-bool]').forEach((node) => {
        const name = node.dataset.rowBool;

        if (spec.fields[name] && node.closest('[data-row]') === row) {
          values[name] = node.querySelector('[data-row-bool-input]').checked;
        }
      });

      return values;
    });
  }

  return null;
};

const collectFields = (container, fields) => {
  const result = {};

  Object.entries(fields).forEach(([name, spec]) => {
    const wrap = container.querySelector(`:scope > [data-field="${name}"]`);

    if (wrap) {
      result[name] = collectField(wrap, spec);
    }
  });

  return result;
};

// ── 미리보기 (A안: 입력값 요약) ─────────────────────────────────────────────

const previewValue = (spec, value) => {
  if (spec.type === 'bool') {
    return value ? '사용' : '사용 안 함';
  }

  if (spec.type === 'choices') {
    return (value || []).map((item) => choiceLabel(item, spec.labels)).join(', ') || '-';
  }

  if (spec.type === 'drinks') {
    return (
      parseDrinks(value)
        .map(({ name, hot, ice }) => `${name}${hot ? ' 🔥' : ''}${ice ? ' 🧊' : ''}`)
        .join(', ') || '-'
    );
  }

  if (spec.type === 'number') {
    const digits = digitsOnly(value);

    return '' === digits ? '-' : withThousands(digits);
  }

  return value === '' || value === undefined || value === null ? '-' : String(value);
};

/**
 * 요금제 행 요약: 타이틀(좌) + 요금(우). 할인요금이 있으면 정상요금에 취소선.
 * title·cost 를 가진 반복 행(가격 정보의 요금제)에만 쓴다.
 *
 * @return {boolean} 요약을 그렸으면 true
 */
const appendPriceRow = (target, fields, row) => {
  if (!fields.title || !fields.cost) {
    return false;
  }

  const line = el('div', 'flex items-center justify-between gap-3');
  const left = el('div', 'flex flex-wrap items-center gap-1.5 font-semibold');
  left.append(el('span', '', String(row?.title ?? '').trim() || '-'));

  if (fields.basic && row?.basic) {
    left.append(
      el(
        'span',
        'rounded bg-neutral-900 px-1.5 py-0.5 text-xs whitespace-nowrap text-white',
        '대표'
      )
    );
  }

  const cost = String(row?.cost ?? '').trim();
  const sale = fields.sale ? String(row?.sale ?? '').trim() : '';
  const right = el('div', 'flex shrink-0 items-center gap-1.5');

  if (sale) {
    right.append(el('s', 'text-neutral-500', cost), el('span', 'font-semibold', sale));
  } else {
    right.append(el('span', 'font-semibold', cost));
  }

  line.append(left, right);
  target.append(line);

  // 요금제 외 필드(설명 등)는 아래에 이어 붙인다
  const rest = Object.fromEntries(
    Object.entries(fields).filter(([name]) => !['title', 'cost', 'sale', 'basic'].includes(name))
  );

  if (Object.keys(rest).length) {
    appendPreviewFields(target, rest, row, 1);
  }

  return true;
};

/**
 * 값 요약을 target 에 붙인다. group 은 펼쳐서 이어 붙이고, repeater(중첩 포함)는
 * 행마다 상자를 만들어 재귀로 내려간다. 빈 값과 꺼진 on/off 는 건너뛴다.
 */
const appendPreviewFields = (target, fields, data, depth = 0) => {
  Object.entries(fields).forEach(([name, spec]) => {
    if ('use' === name) {
      return;
    }

    if ('group' === spec.type) {
      // 그룹 안의 그룹은 제목을 달아 따로 묶는다 (리뷰 이벤트의 네이버 · 구글 등)
      const box = el('div', 'space-y-1 rounded bg-neutral-50 p-2');
      appendPreviewFields(box, spec.fields, data?.[name], depth + 1);

      if (box.children.length) {
        target.append(el('div', 'mt-1 font-semibold', spec.label || name), box);
      }

      return;
    }

    if ('editor' === spec.type) {
      const html = sanitizeModeHTML(data?.[name]?.body || '', data?.[name]?.mode);

      if ('' !== html.trim()) {
        const body = el('div', 'qrshop-intro-preview text-sm');
        body.innerHTML = html;
        target.append(el('div', 'mt-1 font-semibold', spec.label || name), body);
      }

      return;
    }

    if ('gallery' === spec.type) {
      const photos = Array.isArray(data?.[name]) ? data[name] : [];

      if (!photos.length) {
        return;
      }

      const strip = el('div', 'mt-1 flex flex-wrap gap-2');
      photos.forEach((photo) => {
        const image = el('img', 'size-16 rounded-button border border-neutral-200 object-cover');
        image.src = photo.url || '';
        image.alt = '';
        strip.append(image);
      });

      target.append(el('div', 'font-semibold', spec.label || name), strip);
      return;
    }

    if ('repeater' === spec.type) {
      const rows = Array.isArray(data?.[name]) ? data[name] : [];
      const list = el('div', 'space-y-2');

      rows.forEach((row) => {
        // 중첩 단계마다 배경을 바꿔 행 경계를 구분한다
        const box = el(
          'div',
          depth > 0 ? 'rounded border border-neutral-200 bg-white p-2' : 'rounded bg-neutral-50 p-2'
        );

        if (!appendPriceRow(box, spec.fields, row)) {
          appendPreviewFields(box, spec.fields, row, depth + 1);
        }

        if (box.children.length) {
          list.append(box);
        }
      });

      if (list.children.length) {
        // 중첩 반복은 라벨 없이 행만 보여준다
        if (0 === depth) {
          target.append(el('div', 'mt-1 font-semibold', spec.label || name));
        }

        target.append(list);
      }

      return;
    }

    const text = previewValue(spec, data?.[name]);

    if ('-' === text || '사용 안 함' === text) {
      return;
    }

    // 설명글은 줄바꿈을 살려 보여준다
    const line = el('div', 'textarea' === spec.type ? 'whitespace-pre-line' : '');
    line.append(el('strong', 'mr-2', spec.label || name), document.createTextNode(text));
    target.append(line);
  });
};

const renderPreviewSection = (name, spec, value) => {
  // use 토글이 있는 그룹은 꺼져 있으면 표시하지 않는다
  if (spec.type === 'group' && spec.fields.use && !value?.use) {
    return null;
  }

  const section = el('section', 'rounded-button border border-neutral-200 p-4');
  section.append(
    el('h3', 'mb-3 border-b border-neutral-100 pb-2 text-base font-bold', spec.label || name)
  );

  const body = el('div', 'space-y-2 text-sm');

  if ('group' === spec.type) {
    appendPreviewFields(body, spec.fields, value);
  } else if ('repeater' === spec.type) {
    appendPreviewFields(body, { [name]: spec }, { [name]: value });
  } else {
    body.append(el('div', '', previewValue(spec, value)));
  }

  if (!body.children.length) {
    body.append(el('div', 'text-neutral-500', '입력된 내용이 없습니다.'));
  }

  section.append(body);
  return section;
};

// ── 초기화 ──────────────────────────────────────────────────────────────────

const InitShopSetupEditor = () => {
  const root = document.querySelector('[data-qrshop-setup]');
  const editor = root?.querySelector('[data-setup-editor]');

  if (!root || !editor) {
    return;
  }

  // 매장 내용 가져오기(초안 방식)가 이 화면으로 옮기며 맡겨 둔 알림
  showFlash('qrshop_setup_flash');

  const loading = editor.querySelector('[data-setup-editor-loading]');
  const sections = editor.querySelector('[data-setup-editor-sections]');
  const actionbar = root.querySelector('[data-setup-actionbar]');
  const status = root.querySelector('[data-setup-editor-status]');
  const preview = root.querySelector('[data-setup-preview]');
  const previewBody = root.querySelector('[data-setup-preview-body]');

  let schema = {};
  let shopUrl = '';
  // 편집기를 끝까지 그렸는지. 그리다 멈춘 화면(스크립트 오류 등)을 모아 저장하면
  // 못 그린 카드 · 상품이 빈 값으로 초안을 덮어쓰므로, 준비되기 전에는 자동 저장을 하지 않는다
  let editorReady = false;
  // 소개 이미지: 새로 선택(변환)한 경우에만 값이 채워지고, 반영 시에만 전송된다
  let introImageDataUrl = '';
  // 매장 로고: 소개 이미지와 같다 — 새로 고른 경우에만 원본 파일 그대로(data URL) 반영 때 보낸다
  let themeLogoDataUrl = '';

  const statusText = root.querySelector('[data-setup-editor-status-text]');
  const statusToast = root.querySelector('[data-setup-editor-toast]');

  // 공통 메시지(notice) 상태별 수정자. 기본(회색)은 수정자 없이 쓴다.
  const STATUS_CLASSES = {
    success: 'notice--success',
    error: 'notice--error',
    warning: 'notice--warning',
  };

  let statusTimer = null;

  root.querySelector('[data-setup-editor-status-close]')?.addEventListener('click', () => {
    window.clearTimeout(statusTimer);

    if (statusToast) {
      statusToast.hidden = true;
    }
  });

  // ── 순서 변경 모달 (page-setup.php #setup-order-modal) ──
  // 편집기 행을 목록으로 옮겨 적고, [순서 적용] 때 그 순서대로 행을 다시 늘어놓는다.
  // 매장 사이트 저장은 따로 없다 — 행 순서가 곧 저장값이라 [저장] 때 함께 반영된다.
  const orderDialog = root.querySelector('[data-setup-order]');
  const orderList = orderDialog?.querySelector('[data-order-list]');
  const orderTitle = orderDialog?.querySelector('[data-setup-order-title]');
  let orderSource = null;

  // 모달 목록 한 줄 — 번호 · (썸네일) · 제목 · 위/아래 화살표 · (하위 목록)
  // 하위 목록은 자기 [data-order-list] 라서 initOrderList 가 그 안에서만 옮긴다 — 다른 그룹으로 넘어가지 않는다
  const orderItem = ({ title, image, children }, index) => {
    const item = el('li', 'order-list__item');
    item.dataset.id = String(index);

    const num = el('span', 'order-list__num');
    num.setAttribute('aria-hidden', 'true');
    item.append(num);

    if (image) {
      const thumb = el('img', 'order-list__thumb');
      thumb.src = image;
      thumb.alt = '';
      item.append(thumb);
    }

    const up = el('button', 'order-list__move');
    up.type = 'button';
    up.dataset.orderMove = 'up';
    up.setAttribute('aria-label', `「${title}」 위로`);

    const down = el('button', 'order-list__move order-list__move--down');
    down.type = 'button';
    down.dataset.orderMove = 'down';
    down.setAttribute('aria-label', `「${title}」 아래로`);

    item.append(el('span', 'order-list__title', title), up, down);

    if (children?.length) {
      const nested = el('ol', 'order-list order-list--nested');
      nested.dataset.orderList = '';
      nested.setAttribute('aria-label', `「${title}」 하위 항목 순서`);
      children.forEach((child, childIndex) => nested.append(orderItem(child, childIndex)));
      item.classList.add('order-list__item--parent');
      item.append(nested);
    }

    return item;
  };

  // 모달에서 정한 순서를 트리로 읽는다 — [{ index: 처음 위치, children: 같은 모양 | null }]
  const readOrderTree = (list) =>
    readOrder(list).map((index) => {
      const nested = list.querySelector(
        `:scope > .order-list__item[data-id="${index}"] > [data-order-list]`
      );

      return { index, children: nested ? readOrderTree(nested) : null };
    });

  /**
   * 순서 모달을 연다.
   *
   * @param {string} label 모달 제목 앞말
   * @param {{items: () => {title: string, image?: string}[], apply: (order: number[]) => void}} source
   *   items: 지금 순서의 항목(children 이 있으면 하위 목록) / apply: 모달에서 정한 순서 트리(readOrderTree)를 편집기에 옮긴다
   */
  const openOrder = (label, source) => {
    const items = source.items();

    if (!orderDialog || !orderList) {
      return;
    }

    if (items.length < 2) {
      setStatus('순서를 바꾸려면 항목이 2개 이상 있어야 합니다.', 'warning');
      return;
    }

    orderSource = source;
    orderTitle.textContent = `${label} 순서 변경`;
    orderList.textContent = '';
    items.forEach((item, index) => orderList.append(orderItem(item, index)));
    [orderList, ...orderList.querySelectorAll('[data-order-list]')].forEach(syncEdges);
    orderDialog.showModal();
  };

  orderDialog?.querySelector('[data-setup-order-apply]')?.addEventListener('click', () => {
    if (orderSource) {
      orderSource.apply(readOrderTree(orderList));
      // 자동 저장이 듣는 change 를 올려 보낸다
      sections.dispatchEvent(new Event('change'));
      setStatus('순서를 바꿨습니다. [저장]을 눌러야 매장 페이지에 반영됩니다.', 'success');
    }

    orderSource = null;
    orderDialog.close();
  });

  // 형제 노드를 order 순서로 다시 늘어놓는다. 뒤에 붙은 비대상 노드(참고이미지 추가 버튼 등) 앞에 둔다
  const reorderNodes = (nodes, order) => {
    const parent = nodes[0]?.parentElement;
    const anchor = nodes[nodes.length - 1]?.nextSibling || null;

    order.forEach((index) => {
      if (parent && nodes[index]) {
        parent.insertBefore(nodes[index], anchor);
      }
    });
  };

  // 행 안의 반복 필드(가격 정보: 시술 그룹 > 타이틀 및 요금)
  const nestedRowsOf = (row) =>
    row.querySelector(':scope > [data-sub] > [data-type="repeater"] > [data-rows]');

  // 행 자기 제목 — 하위 행의 제목이 먼저 잡히지 않게 가장 가까운 행이 자기인 것만 본다
  const ownRowTitle = (row) =>
    Array.from(row.querySelectorAll('.qrshop-row__title-text'))
      .find((node) => node.closest('[data-row]') === row)
      ?.textContent.trim();

  const rowsTree = (rows) =>
    Array.from(rows.children).map((row, index) => {
      const nested = nestedRowsOf(row);

      return {
        title: ownRowTitle(row) || `항목 ${index + 1}`,
        children: nested?.children.length ? rowsTree(nested) : null,
      };
    });

  const applyRowsTree = (rows, tree) => {
    const nodes = Array.from(rows.children);

    tree.forEach(({ index, children }) => {
      const nested = nodes[index] && children ? nestedRowsOf(nodes[index]) : null;

      if (nested) {
        applyRowsTree(nested, children);
      }
    });

    reorderNodes(
      nodes,
      tree.map(({ index }) => index)
    );
  };

  // 반복 행(FAQ · 가격 정보 · 상품) — 행 제목(bindRowTitle)을 그대로 옮긴다. 2중 반복은 그룹 안에서만 옮긴다
  const rowsOrderSource = (rows) => ({
    items: () => rowsTree(rows),
    apply: (tree) => applyRowsTree(rows, tree),
  });

  // 음료 · 편의 서비스 행 — 이름과 온도 표시
  const drinksOrderSource = (rows, noun) => ({
    items: () =>
      Array.from(rows.children).map((row, index) => {
        const drink = readDrinkRow(row);

        return {
          title: `${drink.name || `${noun} ${index + 1}`}${drink.hot ? ' 🔥' : ''}${drink.ice ? ' 🧊' : ''}`,
        };
      }),
    apply: (tree) =>
      reorderNodes(
        Array.from(rows.children),
        tree.map(({ index }) => index)
      ),
  });

  // 참고이미지 타일 — 썸네일로 구분한다
  const galleryOrderSource = (grid) => {
    const tiles = () =>
      Array.from(grid.querySelectorAll(':scope > [data-photo-id], :scope > [data-photo-temp]'));

    return {
      items: () =>
        tiles().map((tile, index) => ({
          title: `이미지 ${index + 1}${tile.dataset.photoTemp ? ' (저장 대기)' : ''}`,
          image: tile.querySelector('img')?.src || '',
        })),
      apply: (tree) =>
        reorderNodes(
          tiles(),
          tree.map(({ index }) => index)
        ),
    };
  };

  // 카드 본문에서 순서를 바꿀 대상을 찾는다
  const cardOrderSource = (name, spec, body) => {
    const sub = body.querySelector(':scope > [data-sub]');

    if ('drink' === name || 'service' === name) {
      const rows = sub?.querySelector('[data-drink-rows]');
      return rows ? drinksOrderSource(rows, spec.fields.items?.noun || '음료') : null;
    }

    const rows = sub?.querySelector(':scope > [data-type="repeater"] > [data-rows]');
    return rows ? rowsOrderSource(rows) : null;
  };

  // 제목 줄 · 필드 라벨 줄에 붙는 [순서 변경] 아이콘 버튼
  const orderOpenButton = (label, source) => {
    const button = el('button', 'button button--ghost button--sm button--icon');
    button.type = 'button';
    button.title = `${label} 순서 변경`;
    button.setAttribute('aria-haspopup', 'dialog');

    const icon = el('span', 'button__icon button__icon--order');
    icon.setAttribute('aria-hidden', 'true');
    button.append(icon, el('span', 'screen-reader-text', `${label} 순서 변경`));
    button.addEventListener('click', () => openOrder(label, source));
    return button;
  };

  const setStatus = (message, state) => {
    if (!status || !statusText || !statusToast) {
      return;
    }

    statusText.textContent = message;
    status.classList.remove('notice--success', 'notice--error', 'notice--warning');

    if (STATUS_CLASSES[state]) {
      status.classList.add(STATUS_CLASSES[state]);
    }

    // 오류는 화면 낭독기가 바로 읽어 주도록 역할을 바꾼다
    status.setAttribute('role', 'error' === state ? 'alert' : 'status');
    status.setAttribute('aria-live', 'error' === state ? 'assertive' : 'polite');
    statusToast.hidden = '' === message;

    // 오류가 아니면 잠시 뒤 스스로 사라진다
    window.clearTimeout(statusTimer);

    if ('' !== message && 'error' !== state) {
      statusTimer = window.setTimeout(() => {
        statusToast.hidden = true;
      }, 4000);
    }
  };

  showStatus = setStatus;

  // 참고이미지: 선택 즉시 webp 로 변환해 대기시키고, 업로드는 [실제 반영하기] 때 한다
  stageGalleryPhoto = async (file) => {
    if (!file.type.startsWith('image/')) {
      setStatus('이미지 파일만 추가할 수 있습니다.', 'error');
      return null;
    }

    if (file.size > 8 * 1024 * 1024) {
      setStatus('이미지는 8MB 이하만 가능합니다.', 'error');
      return null;
    }

    try {
      const dataUrl = await convertImage(file);
      const temp = `t${++stagedPhotoSeq}`;
      stagedPhotos.set(temp, dataUrl);
      setStatus('사진을 추가했습니다 — [임시 저장] 하면 업로드됩니다.', 'info');

      return { temp, url: dataUrl };
    } catch (error) {
      setStatus(error.message, 'error');

      return null;
    }
  };

  const collect = () => {
    const result = {};

    Object.entries(schema).forEach(([name, spec]) => {
      const card = sections.querySelector(`[data-section="${name}"]`);
      const wrap = card?.querySelector(`:scope > [data-field="${name}"]`);

      if (!wrap) {
        return;
      }

      result[name] = collectField(wrap, spec);

      // 카드 헤더로 올린 사용여부 토글
      const useInput = card.querySelector('[data-use-toggle] [data-input]');

      if (useInput && result[name] && 'object' === typeof result[name]) {
        result[name].use = useInput.checked;
      }
    });

    return result;
  };

  // 설명글 에디터 인스턴스 (카드를 다시 그릴 때마다 새로 만든다)
  let introBody = null;

  // 카드 안 설명글 편집기(editor 타입). Pro 편집기는 보이는 자리에서 띄워야 크기를 제대로 잡으므로
  // 꺼진 카드(hidden) 안의 것은 건너뛰고, 카드를 켤 때 다시 부른다
  const cardEditors = (container) =>
    Array.from(container.querySelectorAll('[data-type="editor"]'))
      .map((wrap) => wrap.modeEditor)
      .filter(Boolean);

  const mountCardEditors = (container) => {
    Array.from(container.querySelectorAll('[data-type="editor"]')).forEach((wrap) => {
      if (!wrap.closest('[hidden]')) {
        wrap.modeEditor?.mount();
      }
    });
  };

  const collectIntro = () => ({
    title: sections.querySelector('[data-intro-title]')?.value.trim() || '',
    body: introBody ? introBody.getHTML() : '',
    mode: introBody ? introBody.getMode() : 'simple',
  });

  const collectSite = () => {
    const result = {};

    SITE_LINKS.forEach(({ key }) => {
      result[key] = sections.querySelector(`[data-site-key="${key}"]`)?.value.trim() || '';
    });

    return result;
  };

  // 매장 테마 — 카드가 없으면(매장 사이트 qrshop-api 가 테마 값을 모르는 옛 버전) null 이라 보내지 않는다
  const collectTheme = () => {
    const card = sections.querySelector('[data-section="__theme"]');

    if (!card) {
      return null;
    }

    const result = { chain: card.querySelector('[data-theme-chain]')?.value.trim() || '' };

    THEME_COLORS.forEach(({ key, fallback }) => {
      result[key] = card.querySelector(`[data-theme-color="${key}"]`)?.value || fallback;
    });

    THEME_SWITCHES.forEach(({ key }) => {
      result[key] = Boolean(card.querySelector(`[data-theme-switch="${key}"]`)?.checked);
    });

    return result;
  };

  // 매장 소개 안내 박스 (굵은 부분 강조)
  const renderIntroNotice = () => {
    const notice = el('p', 'message');

    [
      ['QR Shop은 ', '매장을 방문한 고객을 위한 사이트', '입니다.\n'],
      ['기본적인 네이버의 소개글 보단 방문한 고객을 위한 ', '간단한 인사말', '을 제공하세요.'],
    ].forEach(([before, strong, after]) => {
      notice.append(
        document.createTextNode(before),
        el('strong', 'font-bold', strong),
        document.createTextNode(after)
      );
    });

    return notice;
  };

  // 링크 한 줄: 라벨 줄 오른쪽 글자 버튼(wechat 은 복사) + 입력.
  // 버튼은 값이 있을 때만 보인다.
  const renderSiteLink = ({ key, label, kind }, value) => {
    const isCopy = 'text' === kind;

    const field = el('div', 'field');
    const head = fieldHead(label);
    const action = linkButton(isCopy ? 'ID 복사' : '링크 열기');
    action.title = isCopy ? 'WeChat ID 복사' : `${label} 열기`;
    head.append(action);
    field.append(head);

    const input = el('input', 'w-full px-4');
    input.type = 'text';
    input.dataset.siteKey = key;
    input.placeholder = isCopy ? 'WeChat ID 를 입력하세요.' : 'https://dcraft.co.kr';
    input.value = value || '';
    input.autocapitalize = 'off';
    input.spellcheck = false;

    const refreshAction = () => {
      action.hidden = '' === input.value.trim();
    };

    input.addEventListener('input', refreshAction);
    refreshAction();

    action.addEventListener('click', async () => {
      const current = input.value.trim();

      if ('' === current) {
        return;
      }

      if (isCopy) {
        try {
          await navigator.clipboard.writeText(current);
          setStatus('WeChat ID 를 복사했습니다.', 'success');
        } catch (error) {
          setStatus('복사에 실패했습니다. 직접 선택해 복사해 주세요.', 'error');
        }
        return;
      }

      window.open(
        /^https?:\/\//i.test(current) ? current : `https://${current}`,
        '_blank',
        'noopener'
      );
    });

    field.append(input);
    return field;
  };

  // ── 상품 정보 카드 + 상품 검색 모달 ───────────────────────────────────────

  // 모달은 한 번만 만들어 재사용한다. Add 버튼은 카드의 상품 행을 토글한다.
  let productModal = null;
  let productBrands = null;

  const productModalRefs = () => ({
    brand: productModal.querySelector('[data-product-brand]'),
    keyword: productModal.querySelector('[data-product-keyword]'),
    list: productModal.querySelector('[data-product-list]'),
    total: productModal.querySelector('[data-product-total]'),
    selected: productModal.querySelector('[data-product-selected]'),
  });

  // 카드에 이미 올라간 상품이면 Add 를 활성 상태로 보여준다
  const syncProductModal = () => {
    if (!productModal) {
      return;
    }

    const added = new Set(productRows().map((row) => row.dataset.productId));
    const { selected } = productModalRefs();

    productModal.querySelectorAll('[data-product-add]').forEach((button) => {
      const isAdded = added.has(button.dataset.productAdd);
      button.classList.toggle('qrshop-product-add--on', isAdded);
      button.textContent = 'Add';
      button.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
    });

    selected.textContent = String(added.size);
  };

  const toggleProduct = (product) => {
    const rows = sections.querySelector('[data-product-rows]');
    const existing = rows?.querySelector(`[data-row][data-product-id="${product.product_id}"]`);

    if (existing) {
      existing.remove();
    } else {
      rows?.append(renderProductRow(product));
    }

    syncProductModal();
    syncNavStatus();
  };

  const appendProductResults = (items) => {
    const { list } = productModalRefs();

    items.forEach((product) => {
      const item = el('div', 'qrshop-product-item');

      const thumb = el('div', 'qrshop-product-item__thumb');

      if (product.thumbnail) {
        const image = el('img');
        image.src = product.thumbnail;
        image.alt = product.name || '';
        image.loading = 'lazy';
        thumb.append(image);
      }

      const info = el('div', 'min-w-0 flex-1');

      if (product.brand) {
        info.append(el('span', 'qrshop-product-item__brand', product.brand));
      }

      info.append(el('p', 'qrshop-product-item__name', product.name || ''));

      const addButton = el('button', 'qrshop-product-add', 'Add');
      addButton.type = 'button';
      addButton.dataset.productAdd = String(product.product_id);
      addButton.addEventListener('click', () => toggleProduct(product));

      item.append(thumb, info, addButton);
      list.append(item);
    });

    syncProductModal();
  };

  // 목록은 20개씩, 아래로 스크롤하면 이어서 불러온다
  let productPage = 0;
  let productHasMore = false;
  let productLoading = false;

  const searchProducts = async ({ reset = true } = {}) => {
    if (productLoading) {
      return;
    }

    const { brand, keyword, list, total } = productModalRefs();

    if (reset) {
      productPage = 0;
      productHasMore = false;
      list.textContent = '';
    }

    productLoading = true;
    const notice = el(
      'p',
      'p-4 text-center text-sm text-neutral-500',
      reset ? '상품을 불러오는 중입니다...' : '더 불러오는 중...'
    );
    list.append(notice);

    try {
      const result = await postAjax(root, 'qrshop_setup_products', {
        brand: brand.value,
        q: keyword.value.trim(),
        page: String(productPage + 1),
      });

      notice.remove();

      // 브랜드 목록은 첫 응답으로 한 번만 채운다
      if (null === productBrands) {
        productBrands = result.brands || [];
        productBrands.forEach(({ slug, name }) => {
          const option = el('option', '', name);
          option.value = slug;
          brand.append(option);
        });
      }

      productPage = Number(result.page) || productPage + 1;
      productHasMore = Boolean(result.has_more);
      total.textContent = String(result.total ?? 0);

      const items = result.items || [];
      appendProductResults(items);

      if (!list.querySelector('[data-product-add]')) {
        list.append(el('p', 'p-6 text-center text-sm text-neutral-500', '검색 결과가 없습니다.'));
      }
    } catch (error) {
      notice.remove();
      list.append(el('p', 'p-4 text-center text-sm text-red-600', error.message));
    } finally {
      productLoading = false;
    }
  };

  const buildProductModal = () => {
    const overlay = el('div', 'qrshop-modal');
    overlay.hidden = true;

    const panel = el('div', 'qrshop-modal__panel');

    const head = el('div', 'mb-4 flex items-center justify-between');
    head.append(el('h2', 'text-2xl font-bold text-black', '상품 검색'));

    const closeButton = iconButton(
      'text-neutral-500 hover:text-black',
      '닫기',
      'M6 18L18 6M6 6l12 12'
    );
    head.append(closeButton);

    // 브랜드 + 상품명 검색
    const filters = el('div', 'mb-4 grid gap-2 sm:grid-cols-2');
    const brand = el('select', 'w-full px-4');
    brand.dataset.productBrand = '';
    const anyBrand = el('option', '', '전체 브랜드');
    anyBrand.value = '';
    brand.append(anyBrand);

    // 공통 UI 검색 입력 — 돋보기는 왼쪽 표시, 값이 있으면 오른쪽에 지우기(×), 찾기는 Enter
    const searchWrap = el('span', 'searchfield');
    const searchIcon = el('span', 'searchfield__icon');
    searchIcon.setAttribute('aria-hidden', 'true');

    const keyword = el('input', 'searchfield__input');
    keyword.type = 'search';
    keyword.dataset.productKeyword = '';
    keyword.placeholder = '상품명 입력';

    const searchClear = el('button', 'searchfield__clear');
    searchClear.type = 'button';
    searchClear.dataset.searchClear = '';
    searchClear.setAttribute('aria-label', '지우기');
    searchClear.append(el('span', 'searchfield__clear-icon'));

    searchWrap.append(searchIcon, keyword, searchClear);
    initSearchField(searchWrap);
    filters.append(brand, searchWrap);

    const counts = el('div', 'mb-2 flex items-center justify-between text-sm');
    const totalLine = el('span', 'text-neutral-900');
    totalLine.append(
      document.createTextNode('총 '),
      (() => {
        const node = el('strong', 'text-black', '0');
        node.dataset.productTotal = '';
        return node;
      })(),
      document.createTextNode('개')
    );

    const selectedLine = el('span', 'flex items-center gap-2');
    const selectedBadge = el('span', 'qrshop-product-selected', '0');
    selectedBadge.dataset.productSelected = '';
    selectedLine.append(selectedBadge, el('span', 'text-neutral-900', 'Selected'));
    counts.append(totalLine, selectedLine);

    const list = el('div', 'qrshop-product-list');
    list.dataset.productList = '';

    const doneButton = el(
      'button',
      'button button--primary button--block mt-4 shrink-0',
      '선택 완료'
    );
    doneButton.type = 'button';

    const close = () => {
      overlay.hidden = true;
      document.body.style.overflow = '';
    };

    closeButton.addEventListener('click', close);
    doneButton.addEventListener('click', close);
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        close();
      }
    });

    searchClear.addEventListener('click', () => searchProducts());
    brand.addEventListener('change', () => searchProducts());
    keyword.addEventListener('keydown', (event) => {
      if ('Enter' === event.key) {
        event.preventDefault();
        searchProducts();
      }
    });

    // 목록 끝에 가까워지면 다음 20개를 이어서 불러온다
    list.addEventListener('scroll', () => {
      if (!productHasMore || productLoading) {
        return;
      }

      if (list.scrollTop + list.clientHeight >= list.scrollHeight - 120) {
        searchProducts({ reset: false });
      }
    });

    panel.append(head, filters, counts, list, doneButton);
    overlay.append(panel);
    document.body.append(overlay);
    return overlay;
  };

  const openProductModal = () => {
    if (!productModal) {
      productModal = buildProductModal();
      searchProducts();
    }

    productModal.hidden = false;
    document.body.style.overflow = 'hidden';
    syncProductModal();
    productModalRefs().keyword.focus();
  };

  // 카드에 올라온 상품 행 = 저장 대상. 모달의 Add 는 이 목록을 토글한다.
  const renderProductRow = (product) => {
    const row = el('div', 'qrshop-row qrshop-product-row');
    row.dataset.row = '';
    row.dataset.productId = String(product.product_id);

    const thumb = el('div', 'qrshop-product-row__thumb');

    if (product.thumbnail) {
      const image = el('img');
      image.src = product.thumbnail;
      image.alt = product.name || '';
      image.loading = 'lazy';
      thumb.append(image);
    }

    const body = el('div', 'min-w-0 flex-1 space-y-4');

    // 상품명(오버라이드) + 디자이너 전용 상품
    const orderBadge = rowOrderBadge();

    const nameField = el('div');
    const nameHead = el('div', 'field-head');
    const nameLabel = el('span', 'field-label', '상품');
    nameHead.append(nameLabel);

    const designerToggle = renderMiniToggle(
      product.designer ?? product.default_designer,
      '디자이너 전용'
    );
    designerToggle.querySelector('.toggle__input').dataset.productDesigner = '';
    nameHead.append(designerToggle);

    const nameInput = el('input', 'w-full px-4');
    nameInput.type = 'text';
    nameInput.dataset.productTitle = '';
    nameInput.placeholder = product.name || '상품명을 입력하세요.';
    nameInput.value = product.title || '';
    nameField.append(nameHead, nameInput);

    // 상품은 값을 비워 두면 원본 상품명(placeholder)이 그대로 쓰이므로 제목도 그 이름으로
    bindRowTitle(nameLabel, orderBadge, nameInput, product.name || '');

    // 상품 가격(오버라이드)
    const priceField = el('div');
    priceField.append(fieldHead('상품 가격', "'-' 입력시 가격 숨김"));
    const priceInput = el('input', 'w-full px-4');
    priceInput.type = 'text';
    priceInput.dataset.productPrice = '';
    priceInput.placeholder = product.default_price || '가격을 입력하세요.';
    priceInput.value = product.price || '';
    priceField.append(priceInput);

    body.append(nameField, priceField);

    // 행 액션 — 삭제 (순서 변경은 카드 제목 줄의 순서 모달에서 한다)
    const actions = el('div', 'qrshop-row__actions');

    const remove = removeTextButton('상품 삭제');
    remove.addEventListener('click', async () => {
      if (!(await confirmPop(remove, { message: '목록에서 제외할까요?', confirmLabel: '제외' }))) {
        return;
      }

      const list = row.parentElement;
      row.remove();
      syncProductModal();
      // 자동 저장 · 네비 상태가 알아채도록 (지운 행에서는 이벤트가 올라가지 않는다)
      list?.dispatchEvent(new Event('change', { bubbles: true }));
    });

    actions.append(remove);

    const main = el('div', 'flex items-start gap-4');
    main.append(thumb, body);
    row.append(main, actions);
    return row;
  };

  const productRows = () => Array.from(sections.querySelectorAll('[data-product-rows] [data-row]'));

  const collectProducts = () =>
    productRows().map((row) => ({
      product_id: Number(row.dataset.productId),
      title: row.querySelector('[data-product-title]').value.trim(),
      price: row.querySelector('[data-product-price]').value.trim(),
      designer: row.querySelector('[data-product-designer]').checked,
    }));

  const collectProductRequest = () => sections.querySelector('[data-product-request]')?.value || '';

  const renderProducts = (spec, products) => {
    const card = el('section', 'card');
    card.dataset.section = 'products';

    const head = el('div', 'card__head');
    head.append(el('h2', 'card__title', spec.label || '상품 정보'));
    card.append(head);

    if (spec.instructions) {
      card.append(el('p', 'message', spec.instructions));
    }

    const listHead = el('div', 'mb-2 flex items-center justify-between');
    listHead.append(el('span', 'field-label', '상품'));

    const clearButton = linkButton('전체 삭제');
    listHead.append(clearButton);

    const rows = el('div', 'qrshop-rows space-y-3');
    rows.dataset.productRows = '';

    const headActions = el('div', 'card__head-actions');
    headActions.append(orderOpenButton(spec.label || '상품 정보', rowsOrderSource(rows)));
    head.append(headActions);

    (Array.isArray(products) ? products : []).forEach((product) => {
      rows.append(renderProductRow(product));
    });

    clearButton.addEventListener('click', async () => {
      const count = rows.children.length;

      if (
        !count ||
        !(await confirmPop(clearButton, {
          message: `상품 ${count}개를 모두 목록에서 제외할까요?`,
          confirmLabel: '제외',
        }))
      ) {
        return;
      }

      rows.textContent = '';
      syncProductModal();
      // 자동 저장 · 네비 상태가 알아채도록
      rows.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const addButton = el(
      'button',
      'button button--muted button--block mt-3',
      `+ ${spec.button || '상품 추가'}`
    );
    addButton.type = 'button';
    addButton.addEventListener('click', openProductModal);

    // 추가 요청 사항 — 매장 사이트에는 보내지 않고 automation 초안에만 남는다
    const requestField = el('div', 'mt-6');
    requestField.append(fieldHead('추가 요청 사항'));
    const requestInput = el('textarea', 'w-full px-4');
    requestInput.rows = 4;
    requestInput.dataset.productRequest = '';
    requestInput.placeholder =
      '상품 추가 리스트에 제품이 없거나 추가적으로 요청할 내용을 남겨 주세요.';
    requestField.append(requestInput);

    card.append(listHead, rows, addButton, requestField);
    sections.append(card);
  };

  /**
   * 초안 필드를 매장 저장값 위에 얹는다.
   *
   * 초안이 가진 키만 덮어쓰고, 없는 키는 매장 값을 그대로 쓴다.
   * 스키마에 새 필드가 추가되면(예: 참고이미지) 예전 초안에는 그 키가 없어
   * 통째로 바꿀 경우 값이 사라지기 때문이다.
   */
  const mergeDraftFields = (saved, draft) => {
    if (!draft || 'object' !== typeof draft) {
      return saved || {};
    }

    const isPlainObject = (value) =>
      Boolean(value) && 'object' === typeof value && !Array.isArray(value);
    const result = { ...(saved || {}) };

    Object.entries(draft).forEach(([key, value]) => {
      const base = saved?.[key];

      // 그룹은 서브필드 단위로 합친다. 반복 필드(배열)는 초안이 그대로 이긴다.
      result[key] = isPlainObject(value) && isPlainObject(base) ? { ...base, ...value } : value;
    });

    return result;
  };

  /**
   * 초안 상품 목록을 매장 저장분과 합친다.
   * 초안이 순서·오버라이드를 갖고, 상품 이름·가격 기본값은 매장 응답(또는 검색)에서 온다.
   */
  const mergeDraftProducts = (saved, draft) => {
    const base = new Map((Array.isArray(saved) ? saved : []).map((row) => [row.product_id, row]));

    if (!Array.isArray(draft)) {
      return Array.isArray(saved) ? saved : [];
    }

    return draft
      .map((row) => {
        const found = base.get(row.product_id);

        return found ? { ...found, ...row } : null;
      })
      .filter(Boolean);
  };

  const renderTheme = (theme) => {
    const card = el('section', 'card');
    card.dataset.section = '__theme';
    const head = el('div', 'card__head');
    head.append(el('h2', 'card__title', '매장 테마'));
    card.append(head);

    // 매장 로고 = 매장 글 대표이미지. SVG 가 벡터 그대로 남도록 변환하지 않는다
    const logoField = el('div', 'field');
    logoField.append(fieldHead('매장 로고', 'SVG · WEBP · PNG · JPG'));

    const picker = createImagePicker({
      initialUrl: withVersion(theme?.logo_url, theme?.logo_version),
      types: LOGO_TYPES,
      maxBytes: LOGO_MAX_BYTES,
      desc: 'SVG · WEBP · PNG · JPG, 2MB 이하',
      onStaged: (dataUrl) => {
        themeLogoDataUrl = dataUrl || '';

        if (dataUrl) {
          setStatus('로고 준비 완료 — [실제 반영하기] 시 교체됩니다.', 'info');
        }
      },
      onError: (message) => setStatus(message, 'error'),
    });

    logoField.append(picker.element);

    const chainField = el('div', 'field');
    chainField.append(fieldHead('체인점명', '체인점명을 노출해야 할 경우 입력'));
    const chainInput = el('input', 'w-full px-4');
    chainInput.type = 'text';
    chainInput.dataset.themeChain = '';
    chainInput.placeholder = '체인점명을 입력하세요.';
    chainInput.value = theme?.chain || '';
    chainField.append(chainInput);

    const colors = el('div', 'grid gap-x-4 sm:grid-cols-3');
    THEME_COLORS.forEach((color) => colors.append(renderColorField(color, theme?.[color.key])));

    card.append(logoField, chainField, colors);

    // 사용 여부 스위치 — 라벨(좌) · 스위치(우)
    THEME_SWITCHES.forEach(({ key, label }) => {
      const field = el('div', 'field');
      const switchHead = fieldHead(label);
      const toggle = renderToggle(theme?.[key]);
      const input = toggle.querySelector('input');
      input.dataset.themeSwitch = key;
      input.setAttribute('aria-label', `${label} 사용`);
      switchHead.append(toggle);
      field.append(switchHead);
      card.append(field);
    });

    sections.append(card);
  };

  const renderIntro = (intro, site) => {
    introBody?.destroy();
    introBody = null;
    const card = el('section', 'card');
    card.dataset.section = '__intro';
    const head = el('div', 'card__head');
    head.append(el('h2', 'card__title', '매장 소개'));
    card.append(head, renderIntroNotice());

    // 대표 사진 — 공용 이미지 선택 컴포넌트
    const imageField = el('div', 'field');
    imageField.append(fieldHead('대표 사진', '기본 네이버 첫 이미지 / 변경 원할 경우 업로드'));

    const picker = createImagePicker({
      initialUrl: withVersion(intro?.image_url, intro?.image_version),
      onStaged: (dataUrl) => {
        introImageDataUrl = dataUrl || '';

        if (dataUrl) {
          setStatus('소개 이미지 준비 완료 — [실제 반영하기] 시 교체됩니다.', 'info');
        }
      },
      onError: (message) => setStatus(message, 'error'),
    });

    imageField.append(picker.element);

    // 소개 타이틀 (h3 로 저장)
    const titleField = el('div', 'field');
    titleField.append(fieldHead('매장 소개 타이틀', '소제목(H3)으로 저장'));
    const titleInput = el('input', 'w-full px-4');
    titleInput.type = 'text';
    titleInput.dataset.introTitle = '';
    titleInput.placeholder = '매장 소개 간단한 타이틀 입력 하세요.';
    titleInput.value = intro?.title || '';
    titleField.append(titleInput);

    // 설명글 — Simple(리치 텍스트) / Pro(워드프레스 편집기). 저장 HTML 이 본문에 그대로 들어간다.
    // 제목 줄 오른쪽이 모드 전환 버튼이다
    const bodyField = el('div', 'field');
    introBody = createModeEditor({
      html: intro?.body || '',
      mode: intro?.mode || 'simple',
      placeholder: '방문한 고객에게 전할 인사말을 입력하세요.',
    });
    const bodyHead = fieldHead('매장 설명글');
    bodyHead.append(introBody.switcher);
    bodyField.append(bodyHead, introBody.element);

    card.append(imageField, titleField, bodyField);

    // 매장 링크 (salon `site` 그룹)
    SITE_LINKS.forEach((link) => card.append(renderSiteLink(link, site?.[link.key])));

    sections.append(card);
  };

  const render = (data, intro, site, products, theme) => {
    cardEditors(sections).forEach((editor) => editor.destroy());
    sections.textContent = '';

    // 메뉴 숨김 · 노출 처리 — 가장 위에 별도 스타일로
    NAV_FIELDS.forEach((navField) => {
      const navSpec = schema[navField];

      if (!navSpec) {
        return;
      }

      const card = el('section', 'card');
      card.dataset.section = navField;
      const head = el('div', 'card__head');
      head.append(el('h2', 'card__title', navSpec.label || navField));
      card.append(head, renderNavField(navField, navSpec, data?.[navField]));
      sections.append(card);
    });

    if (theme) {
      renderTheme(theme);
    }

    renderIntro(intro, site);

    Object.entries(schema).forEach(([name, spec]) => {
      if (NAV_FIELDS.includes(name)) {
        return;
      }

      // 상품 정보는 상품 검색 모달을 쓰는 전용 카드
      if ('products' === spec.type) {
        renderProducts(spec, products);
        return;
      }

      const card = el('section', 'card');
      card.dataset.section = name;

      const head = el('div', 'card__head');
      head.append(el('h2', 'card__title', spec.label || name));

      // 사용여부(use)는 카드 헤더 우측 토글로 올린다
      const useSpec = 'group' === spec.type ? spec.fields.use : null;
      const body = renderField(name, spec, data?.[name], {
        skipUse: Boolean(useSpec),
        popConfirm: POP_CONFIRM_FIELDS.includes(name),
      });

      // 제목 줄 우측 묶음 — [순서 변경] 버튼 · 사용여부 스위치
      const headActions = el('div', 'card__head-actions');
      const source = ORDER_FIELDS.includes(name) ? cardOrderSource(name, spec, body) : null;
      const orderButton = source ? orderOpenButton(spec.label || name, source) : null;

      if (orderButton) {
        headActions.append(orderButton);
      }

      // 참고이미지(gallery)는 라벨 줄 오른쪽에 따로 붙인다
      body
        .querySelectorAll(':scope > [data-sub] > [data-type="gallery"]')
        .forEach((galleryWrap) => {
          const galleryHead = galleryWrap.querySelector(':scope > .field-head');
          const grid = galleryWrap.querySelector(':scope > [data-gallery]');
          const galleryLabel = galleryHead?.querySelector('.field-label')?.textContent || '이미지';

          if (!galleryHead || !grid) {
            return;
          }

          const tools = el('div', 'flex items-center gap-2');
          tools.append(
            ...galleryHead.querySelectorAll(':scope > .field-hint'),
            orderOpenButton(galleryLabel, galleryOrderSource(grid))
          );
          galleryHead.append(tools);
        });

      // ACF instructions 가 있으면 카드 안내 박스로 노출
      const notice = spec.instructions ? el('p', 'message', spec.instructions) : null;

      if (useSpec) {
        // 스위치 자체가 ON/OFF 를 보여 주므로 '사용여부' 같은 이름표는 붙이지 않는다
        const toggle = renderToggle(data?.[name]?.use);
        toggle.dataset.useToggle = '';

        // OFF 면 타이틀 줄만 남기고 입력 영역을 접는다.
        // 수집은 DOM 값을 직접 읽으므로 숨긴 상태에서도 저장값은 유지된다.
        const useInput = toggle.querySelector('[data-input]');
        const syncOffState = () => {
          const off = !useInput.checked;
          card.classList.toggle('card--off', off);
          body.hidden = off;

          // 꺼진 카드는 본문이 없으니 순서 버튼도 감춘다
          if (orderButton) {
            orderButton.hidden = off;
          }

          if (notice) {
            notice.hidden = off;
          }

          if (!off && editorReady) {
            mountCardEditors(body);
          }
        };

        useInput.addEventListener('change', syncOffState);
        syncOffState();

        headActions.append(toggle);
      }

      if (headActions.children.length) {
        head.append(headActions);
      }

      card.append(head);

      if (notice) {
        card.append(notice);
      }

      card.append(body);
      sections.append(card);
    });
  };

  /**
   * 매장 데이터를 불러와 편집기를 렌더한다.
   * useDraft=false 면 임시 저장본을 무시하고 매장 사이트의 현재 값만 쓴다.
   */
  const loadEditor = async ({ useDraft = true, silent = false } = {}) => {
    editorReady = false;

    try {
      const result = await postAjax(root, 'qrshop_setup_data', {});
      schema = result.schema || {};
      shopUrl = result.shop?.url || '';

      // 매장 사이트 열기는 오른쪽 아래 퀵 버튼이 맡는다 — 로그인 직후 첫 화면은 아직 주소를 몰라 감춰져 있다
      const quickLink = document.querySelector('[data-quickbar-site]');

      if (quickLink && shopUrl) {
        quickLink.href = shopUrl;
        quickLink.hidden = false;
      }
      introImageDataUrl = '';
      themeLogoDataUrl = '';

      // 초안은 담긴 것이 하나라도 있으면 쓴다 — 매장 내용 가져오기가 fields 를 비워도
      // 소개글 · 사이트 · 테마 · 상품의 반영하지 않은 수정은 남아 있을 수 있다
      const filled = (value) => Boolean(value) && Object.keys(value).length > 0;
      const stored = result.draft;
      const draft =
        useDraft &&
        stored &&
        (filled(stored.fields) ||
          filled(stored.intro) ||
          filled(stored.site) ||
          filled(stored.theme) ||
          filled(stored.products) ||
          Boolean(stored.product_request))
          ? stored
          : null;

      render(
        mergeDraftFields(result.fields, draft?.fields),
        draft?.intro ? { ...result.intro, ...draft.intro } : result.intro,
        draft?.site ? { ...result.site, ...draft.site } : result.site,
        mergeDraftProducts(result.products, draft?.products),
        result.theme && draft?.theme ? { ...result.theme, ...draft.theme } : result.theme
      );

      // 상품 추가 요청 사항 — 초안에만 남는 값
      const requestInput = sections.querySelector('[data-product-request]');

      if (requestInput && draft?.product_request) {
        requestInput.value = draft.product_request;
      }

      loading.hidden = true;
      sections.hidden = false;

      // Pro 로 저장된 설명글 — 워드프레스 편집기는 보이는 자리에서 띄워야 크기를 제대로 잡는다
      introBody?.mount();
      mountCardEditors(sections);
      actionbar.hidden = false;

      // 좌측 퀵 네비 (모바일: 상단 칩) — 렌더된 섹션 순서 그대로
      const navEl = root.querySelector('[data-setup-nav]');
      if (navEl) {
        navLinks = initScrollSpyNav(
          navEl,
          Array.from(sections.querySelectorAll('[data-section]')).map((card) => ({
            el: card,
            label: card.querySelector('h2')?.textContent || card.dataset.section,
          }))
        );
        navEl.hidden = false;
        syncNavStatus();
      }

      // 다시 불러온 값이 곧바로 자동저장으로 덮어써지지 않게 기준을 맞춘다
      autosaved = JSON.stringify(savePayload());
      editorReady = true;

      if (silent) {
        setAutosaveNote('');
      } else if (!useDraft) {
        setStatus('매장 사이트의 현재 값을 불러왔습니다.', 'success');
        setAutosaveNote('');
      } else if (draft && !draft.applied_at) {
        setStatus(`반영하지 않은 임시 저장본(${draft.saved_at})을 불러왔습니다.`, 'info');
      }
    } catch (error) {
      loading.textContent = error.message;
      setStatus(error.message, 'error');
    }
  };

  // 원본에서 가져오기 — 초안을 무시하고 매장 사이트 값으로 화면을 되돌린다
  root.querySelector('[data-setup-reload]')?.addEventListener('click', async (event) => {
    if (
      !window.confirm(
        '임시 저장한 내용을 화면에서 버리고 매장 사이트의 현재 값을 다시 불러옵니다. 계속할까요?'
      )
    ) {
      return;
    }

    window.clearTimeout(autosaveTimer);
    const release =
      event.currentTarget instanceof HTMLButtonElement
        ? busy(event.currentTarget, '불러오는 중…')
        : () => {};
    setStatus('매장 정보를 다시 불러오는 중...', 'info');

    await loadEditor({ useDraft: false });

    release();
  });

  loadEditor();

  // ── 네비 섹션 상태 (입력 있음 / 비어 있음 / OFF) ──────────────────────────

  let navLinks = new Map();

  /** 카드에 입력값이 하나라도 있는지 — 텍스트·선택·이미지·상품 행을 본다. */
  const cardHasInput = (card) => {
    const typed = Array.from(
      card.querySelectorAll('input:not([type="checkbox"]), textarea, select')
    ).some((node) => '' !== node.value.trim());

    if (typed) {
      return true;
    }

    const checked = Array.from(card.querySelectorAll('input[type="checkbox"]:checked')).some(
      (node) => !node.closest('[data-use-toggle]')
    );

    if (checked) {
      return true;
    }

    // 매장 소개 이미지 / 상품 썸네일
    return Array.from(card.querySelectorAll('img')).some(
      (image) => !image.hidden && image.getAttribute('src')
    );
  };

  const syncNavStatus = () => {
    navLinks.forEach((link, card) => {
      const dot = link.querySelector('[data-nav-dot]');

      if (!dot) {
        return;
      }

      // 점은 사용 여부 스위치를 따라간다. 스위치가 없는 영역은 항상 켜진 것으로 본다.
      const useInput = card.querySelector('[data-use-toggle] [data-input]');
      const state = !useInput || useInput.checked ? 'on' : 'off';

      dot.dataset.state = state;
      dot.setAttribute('title', 'on' === state ? '사용함' : '사용 안 함');
    });
  };

  // ── 임시 저장 (수동 + 자동) ────────────────────────────────────────────────

  /**
   * 갤러리에서 아직 업로드하지 않은 사진(data URL)을 값에서 걷어낸다.
   * 초안은 옵션 한 칸에 들어가므로 base64 를 담지 않는다.
   */
  const stripStagedPhotos = (spec, value) => {
    if (!value || 'object' !== typeof value) {
      return value;
    }

    if ('gallery' === spec.type) {
      return (Array.isArray(value) ? value : []).filter((photo) => photo?.id);
    }

    if ('group' === spec.type) {
      const result = { ...value };
      Object.entries(spec.fields).forEach(([name, sub]) => {
        result[name] = stripStagedPhotos(sub, result[name]);
      });

      return result;
    }

    if ('repeater' === spec.type) {
      return (Array.isArray(value) ? value : []).map((row) => {
        const result = { ...row };
        Object.entries(spec.fields).forEach(([name, sub]) => {
          result[name] = stripStagedPhotos(sub, result[name]);
        });

        return result;
      });
    }

    return value;
  };

  /** withPhotoData=true 는 반영용 — 대기 중인 사진의 data URL 까지 담는다. */
  const savePayload = ({ withPhotoData = false } = {}) => {
    const fields = collect();

    if (!withPhotoData) {
      Object.entries(schema).forEach(([name, spec]) => {
        fields[name] = stripStagedPhotos(spec, fields[name]);
      });
    }

    return {
      fields: JSON.stringify(fields),
      intro: JSON.stringify(collectIntro()),
      site: JSON.stringify(collectSite()),
      theme: JSON.stringify(collectTheme()),
      products: JSON.stringify(collectProducts()),
      product_request: collectProductRequest(),
    };
  };

  const autosaveNote = root.querySelector('[data-setup-autosave]');
  const AUTOSAVE_DELAY = 3000;
  let autosaveTimer = null;
  let autosaved = '';
  let saving = false;

  const setAutosaveNote = (message) => {
    if (autosaveNote) {
      autosaveNote.textContent = message;
    }
  };

  const savedAtLabel = () =>
    new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });

  function scheduleAutosave() {
    window.clearTimeout(autosaveTimer);
    autosaveTimer = window.setTimeout(autosave, AUTOSAVE_DELAY);
  }

  /** 마지막 저장 이후 바뀐 게 있을 때만 저장한다. */
  async function autosave() {
    if (!editorReady) {
      return;
    }

    if (saving) {
      // 수동 저장·반영 중이면 끝난 뒤 다시 시도
      scheduleAutosave();
      return;
    }

    const payload = savePayload();
    const snapshot = JSON.stringify(payload);

    if (snapshot === autosaved) {
      return;
    }

    saving = true;
    setAutosaveNote('자동 저장 중...');

    try {
      await postAjax(root, 'qrshop_setup_save', payload);
      autosaved = snapshot;
      setAutosaveNote(`자동 저장됨 ${savedAtLabel()}`);
    } catch (error) {
      setAutosaveNote('자동 저장 실패 — [임시 저장]을 눌러 주세요');
    } finally {
      saving = false;
    }
  }

  // 입력이 멈추면 저장. 동적으로 추가되는 행·모달 선택도 위임으로 함께 잡는다.
  const onEdit = () => {
    scheduleAutosave();
    syncNavStatus();
  };

  sections.addEventListener('input', onEdit);
  sections.addEventListener('change', onEdit);

  // 페이지를 떠날 때 남은 변경분은 beacon 으로 흘려보낸다(응답을 기다리지 않는다)
  const flushAutosave = () => {
    if (!editorReady) {
      return;
    }

    const payload = savePayload();

    if (JSON.stringify(payload) === autosaved || !navigator.sendBeacon) {
      return;
    }

    window.clearTimeout(autosaveTimer);
    const body = new URLSearchParams({
      action: 'qrshop_setup_save',
      nonce: root.dataset.nonce,
      ...payload,
    });
    navigator.sendBeacon(
      root.dataset.ajaxUrl,
      new Blob([body.toString()], { type: 'application/x-www-form-urlencoded' })
    );
  };

  // 업로드하지 않은 사진이 남아 있으면 이탈 시 경고
  window.addEventListener('beforeunload', (event) => {
    if (stagedPhotos.size) {
      event.preventDefault();
      event.returnValue = '';
    }
  });

  window.addEventListener('pagehide', flushAutosave);
  document.addEventListener('visibilitychange', () => {
    if ('hidden' === document.visibilityState) {
      flushAutosave();
    }
  });

  /**
   * 대기 중인 사진을 업로드해 타일을 첨부 ID 로 승격한다.
   * [임시 저장] 에서만 호출한다 — 자동저장은 사진을 올리지 않는다.
   *
   * @return {Promise<number>} 업로드 실패한 장수
   */
  const uploadStagedPhotos = async () => {
    const tiles = Array.from(sections.querySelectorAll('[data-gallery] [data-photo-temp]'));

    if (!tiles.length) {
      return 0;
    }

    setStatus(`사진 ${tiles.length}장 업로드 중...`, 'info');
    let failed = 0;

    for (const tile of tiles) {
      const temp = tile.dataset.photoTemp;
      const dataUrl = stagedPhotos.get(temp);

      if (!dataUrl) {
        continue;
      }

      try {
        // 순서를 유지하려고 하나씩 올린다
        // eslint-disable-next-line no-await-in-loop
        const result = await postAjax(root, 'qrshop_setup_photo', { image: dataUrl });
        const id = Number(result.id);

        if (!id) {
          throw new Error('업로드한 이미지 정보를 받지 못했습니다.');
        }

        delete tile.dataset.photoTemp;
        tile.dataset.photoId = String(id);
        tile.querySelector('.qrshop-gallery__badge')?.remove();
        tile.querySelector('img').src = result.url || dataUrl;
        stagedPhotos.delete(temp);
      } catch (error) {
        failed += 1;
      }
    }

    return failed;
  };

  root.querySelector('[data-setup-save]')?.addEventListener('click', async (event) => {
    window.clearTimeout(autosaveTimer);
    const release =
      event.currentTarget instanceof HTMLButtonElement
        ? busy(event.currentTarget, '저장 중…')
        : () => {};
    saving = true;
    setStatus('임시 저장 중...', 'info');

    try {
      // 대기 사진을 먼저 올려 초안에 ID 로 남긴다 (새로고침해도 유지)
      const failed = await uploadStagedPhotos();
      const payload = savePayload();
      const result = await postAjax(root, 'qrshop_setup_save', payload);
      autosaved = JSON.stringify(payload);
      setStatus(
        failed
          ? `임시 저장했습니다. 사진 ${failed}장은 업로드하지 못했습니다. (${result.saved_at})`
          : `임시 저장했습니다. (${result.saved_at})`,
        failed ? 'error' : 'success'
      );
      setAutosaveNote(`저장됨 ${savedAtLabel()}`);
    } catch (error) {
      setStatus(error.message, 'error');
    } finally {
      saving = false;
      release();
    }
  });

  root.querySelector('[data-setup-apply]')?.addEventListener('click', async (event) => {
    if (!window.confirm('입력한 내용을 실제 매장 사이트에 반영합니다. 계속할까요?')) {
      return;
    }

    window.clearTimeout(autosaveTimer);
    saving = true;
    const release =
      event.currentTarget instanceof HTMLButtonElement
        ? busy(event.currentTarget, '반영 중…')
        : () => {};
    setStatus('매장 사이트에 반영 중...', 'info');

    try {
      await postAjax(root, 'qrshop_setup_apply', {
        ...savePayload({ withPhotoData: true }),
        intro_image: introImageDataUrl,
        theme_logo: themeLogoDataUrl,
      });
      introImageDataUrl = '';
      themeLogoDataUrl = '';
      stagedPhotos.clear();

      // 새로 만들어진 첨부 ID 등 실제 저장 결과로 화면을 맞춘다
      await loadEditor({ useDraft: false, silent: true });
      autosaved = JSON.stringify(savePayload());
      setStatus('반영 완료! 매장 페이지에서 확인 해 보세요.', 'success');
    } catch (error) {
      setStatus(error.message, 'error');
    } finally {
      saving = false;
      release();
    }
  });

  // 매장 테마 요약 (로고 + 체인점명 + 색 + 사용 여부)
  const renderThemePreview = () => {
    const theme = collectTheme();

    if (!theme) {
      return null;
    }

    const section = el('section', 'rounded-button border border-neutral-200 p-4');
    section.append(
      el('h3', 'mb-3 border-b border-neutral-100 pb-2 text-base font-bold', '매장 테마')
    );

    const logo = sections.querySelector('[data-section="__theme"] img');
    if (logo && !logo.hidden && logo.getAttribute('src')) {
      const img = el('img', 'mb-3 h-12 w-auto max-w-[210px] object-contain');
      img.src = logo.src;
      section.append(img);
    }

    const list = el('ul', 'space-y-1 text-sm');
    const line = (label, node) => {
      const li = el('li', 'flex items-center gap-2');
      li.append(el('strong', '', label), node);
      list.append(li);
    };

    if (theme.chain) {
      line('체인점명', document.createTextNode(theme.chain));
    }

    THEME_COLORS.forEach(({ key, label }) => {
      const value = el('span', 'inline-flex items-center gap-1.5 uppercase');
      const chip = el('span', 'inline-block size-4 rounded-sm border border-neutral-200');
      chip.style.backgroundColor = theme[key];
      value.append(chip, document.createTextNode(theme[key]));
      line(label, value);
    });

    THEME_SWITCHES.forEach(({ key, label }) => {
      line(label, document.createTextNode(theme[key] ? '사용함' : '사용 안 함'));
    });

    section.append(list);
    return section;
  };

  // 매장 소개 요약 (이미지 + h3 + 문단 + 링크)
  const renderIntroPreview = () => {
    const intro = collectIntro();
    const introSection = el('section', 'rounded-button border border-neutral-200 p-4');
    introSection.append(
      el('h3', 'mb-3 border-b border-neutral-100 pb-2 text-base font-bold', '매장 소개')
    );

    const introImage = sections.querySelector('[data-section="__intro"] img');
    if (introImage && !introImage.hidden && introImage.getAttribute('src')) {
      const img = el('img', 'mb-3 max-h-56 rounded-button object-cover');
      img.src = introImage.src;
      introSection.append(img);
    }

    if (intro.title) {
      introSection.append(el('h4', 'mb-2 text-lg font-bold', intro.title));
    }

    // 설명글 — 에디터 HTML 을 그대로(정제 후) 보여준다
    const bodyHtml = sanitizeModeHTML(intro.body, intro.mode);

    if ('' !== bodyHtml) {
      const body = el('div', 'qrshop-intro-preview text-sm');
      body.innerHTML = bodyHtml;
      introSection.append(body);
    }

    // 매장 링크 — 입력된 것만 요약에 표시
    const site = collectSite();
    const filledLinks = SITE_LINKS.filter(({ key }) => site[key]);

    if (filledLinks.length) {
      const list = el('ul', 'mt-3 space-y-1 border-t border-neutral-100 pt-3 text-sm');
      filledLinks.forEach(({ key, label }) => {
        const li = el('li');
        li.append(el('strong', 'mr-2', label), document.createTextNode(site[key]));
        list.append(li);
      });
      introSection.append(list);
    }

    return introSection;
  };

  // 상품 정보 요약 — 오버라이드가 없으면 상품 기본값을 보여준다
  const renderProductsPreview = (spec) => {
    const rows = productRows();
    const request = collectProductRequest().trim();

    if (!rows.length && '' === request) {
      return null;
    }

    const section = el('section', 'rounded-button border border-neutral-200 p-4');
    section.append(
      el(
        'h3',
        'mb-3 border-b border-neutral-100 pb-2 text-base font-bold',
        spec.label || '상품 정보'
      )
    );

    const list = el('div', 'space-y-2 text-sm');

    rows.forEach((row) => {
      const box = el('div', 'flex items-center justify-between gap-3 rounded bg-neutral-50 p-2');
      const titleInput = row.querySelector('[data-product-title]');
      const priceInput = row.querySelector('[data-product-price]');
      const price = priceInput.value.trim() || priceInput.placeholder;

      const left = el('div', 'flex flex-wrap items-center gap-1.5 font-semibold');
      left.append(el('span', '', titleInput.value.trim() || titleInput.placeholder));

      if (row.querySelector('[data-product-designer]').checked) {
        left.append(
          el(
            'span',
            'rounded bg-neutral-900 px-1.5 py-0.5 text-xs whitespace-nowrap text-white',
            '디자이너 전용'
          )
        );
      }

      box.append(left, el('div', 'shrink-0', '-' === price ? '가격 숨김' : price));
      list.append(box);
    });

    if ('' !== request) {
      const note = el('div', 'mt-3 border-t border-neutral-100 pt-3 whitespace-pre-line');
      note.append(el('strong', 'mr-2', '추가 요청 사항'), document.createTextNode(request));
      list.append(note);
    }

    section.append(list);
    return section;
  };

  root.querySelector('[data-setup-preview-open]')?.addEventListener('click', () => {
    previewBody.textContent = '';
    const data = collect();

    // 순서는 편집 화면의 카드 순서를 그대로 따른다
    Array.from(sections.querySelectorAll('[data-section]')).forEach((card) => {
      const name = card.dataset.section;

      if ('__intro' === name) {
        previewBody.append(renderIntroPreview());
        return;
      }

      if ('__theme' === name) {
        const themeSection = renderThemePreview();

        if (themeSection) {
          previewBody.append(themeSection);
        }

        return;
      }

      const spec = schema[name];

      if (!spec) {
        return;
      }

      const section =
        'products' === spec.type
          ? renderProductsPreview(spec)
          : renderPreviewSection(name, spec, data[name]);

      if (section) {
        previewBody.append(section);
      }
    });

    preview.hidden = false;
    document.body.style.overflow = 'hidden';
  });

  const closePreview = () => {
    preview.hidden = true;
    document.body.style.overflow = '';
  };

  root.querySelector('[data-setup-preview-close]')?.addEventListener('click', closePreview);
  preview?.addEventListener('click', (event) => {
    if (event.target === preview) {
      closePreview();
    }
  });
};

export default InitShopSetupEditor;
